import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { ShoppingCart, User, LogOut, Package, Menu, X, Shield, ClipboardList, Clock, Zap, Mail, Eye, Tag, Star, Lock, Layers, Users, Camera, Palette, FileText } from "lucide-react";
import { useCategories } from "@/hooks/useCategories";
import { useSiteContent } from "@/hooks/useSiteContent";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";

const LOGO_URL = "/images/logo.jpg";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { categories } = useCategories();
  const { totalItems } = useCart();
  const content = useSiteContent();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      window.scrollTo({ top: 0 });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#050505" }}>
      {/* Header */}
      <header
        data-testid="main-header"
        className="sticky top-0 z-50 border-b border-[#1A1A1A]"
        style={{ backgroundColor: "#050505" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <a href="/" onClick={handleLogoClick} data-testid="logo-link" className="flex-shrink-0 cursor-pointer">
              <img
                src={LOGO_URL}
                alt="GameBoxMods.com"
                className="h-10 sm:h-14 w-auto"
              />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {categories.map(cat => (
                <Link
                  key={cat.key}
                  to={cat.key === "design" ? "/design-request" : `/products?category=${cat.key}`}
                  data-testid={`nav-${cat.key}`}
                  className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1] hover:text-[#39FF14] transition-colors duration-200"
                >
                  {cat.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Cart */}
              <Link to="/cart" data-testid="cart-link" className="relative group">
                <div className="p-2 border border-[#1A1A1A] hover:border-[#39FF14] transition-colors duration-200">
                  <ShoppingCart className="h-5 w-5 text-[#A1A1A1] group-hover:text-[#39FF14]" strokeWidth={1.5} />
                  {totalItems > 0 && (
                    <span
                      data-testid="cart-count"
                      className="absolute -top-1 -right-1 bg-[#39FF14] text-[#050505] text-[10px] font-bold font-mono w-5 h-5 flex items-center justify-center"
                    >
                      {totalItems}
                    </span>
                  )}
                </div>
              </Link>

              {/* Auth */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="user-menu-trigger"
                      variant="outline"
                      className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] hover:bg-transparent text-[#A1A1A1] hover:text-[#39FF14] rounded-none px-3"
                    >
                      <User className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      <span className="hidden sm:inline font-mono text-xs uppercase tracking-wider">{user.name || user.email}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="bg-[#0D0D0D] border-[#1A1A1A] rounded-none min-w-[180px]"
                    align="end"
                  >
                    {user.role === "admin" && (
                      <>
                        <DropdownMenuItem
                          data-testid="menu-admin"
                          onClick={() => navigate("/admin")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Shield className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Products
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-orders"
                          onClick={() => navigate("/admin/orders")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <ClipboardList className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Orders
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-waitlist"
                          onClick={() => navigate("/admin/waitlist")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Clock className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Waitlist
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-preorders"
                          onClick={() => navigate("/admin/preorders")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Zap className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Preorders
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-emails"
                          onClick={() => navigate("/admin/emails")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Mail className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Messages
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-visitors"
                          onClick={() => navigate("/admin/visitors")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Eye className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Visitors
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-coupons"
                          onClick={() => navigate("/admin/coupons")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Tag className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Discount Codes
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-categories"
                          onClick={() => navigate("/admin/categories")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Layers className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Categories
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-reviews"
                          onClick={() => navigate("/admin/reviews")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Star className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Reviews
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-users"
                          onClick={() => navigate("/admin/users")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Users className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Users
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-showcase"
                          onClick={() => navigate("/admin/showcase")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Camera className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Photo Showcase
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-design-requests"
                          onClick={() => navigate("/admin/design-requests")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <Palette className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Design Requests
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid="menu-admin-site-content"
                          onClick={() => navigate("/admin/site-content")}
                          className="text-[#39FF14] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                        >
                          <FileText className="h-4 w-4 mr-2" strokeWidth={1.5} />
                          Site Content
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-[#1A1A1A]" />
                      </>
                    )}
                    <DropdownMenuItem
                      data-testid="menu-orders"
                      onClick={() => navigate("/orders")}
                      className="text-[#F2F2F2] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                    >
                      <Package className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      My Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="menu-change-password"
                      onClick={() => navigate("/change-password")}
                      className="text-[#F2F2F2] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                    >
                      <Lock className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Change Password
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#1A1A1A]" />
                    <DropdownMenuItem
                      data-testid="menu-logout"
                      onClick={handleLogout}
                      className="text-[#F2F2F2] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                    >
                      <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login" data-testid="login-link">
                  <Button
                    variant="outline"
                    className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] hover:bg-transparent text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
                  >
                    Sign In
                  </Button>
                </Link>
              )}

              {/* Mobile menu */}
              <button
                data-testid="mobile-menu-toggle"
                className="md:hidden p-2 border border-[#1A1A1A] hover:border-[#39FF14] transition-colors"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen ? <X className="h-5 w-5 text-[#A1A1A1]" /> : <Menu className="h-5 w-5 text-[#A1A1A1]" />}
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          {mobileOpen && (
            <nav className="md:hidden border-t border-[#1A1A1A] py-4 flex flex-col gap-4" data-testid="mobile-nav">
              {categories.map(c => ({ to: c.key === "design" ? "/design-request" : `/products?category=${c.key}`, label: c.label })).map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1] hover:text-[#39FF14] transition-colors pl-2"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Main */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="border-t border-[#1A1A1A] py-16" style={{ backgroundColor: "#050505" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            <div>
              <a href="/" onClick={handleLogoClick} className="cursor-pointer">
                <img src={LOGO_URL} alt="GameBoxMods.com" className="h-10 mb-4" />
              </a>
              <p className="text-[#A1A1A1] text-sm max-w-xs">
                {content.footer_tagline}
              </p>
              <p className="text-[#A1A1A1]/60 text-[10px] font-mono uppercase tracking-wider mt-3 max-w-xs" data-testid="trademark-notice">
                {content.footer_trademark}
              </p>
            </div>
            <div className="flex gap-16">
              <div>
                <h4 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14] mb-4">Shop</h4>
                <div className="flex flex-col gap-2">
                  {categories.map(cat => (
                    <Link key={cat.key} to={`/products?category=${cat.key}`} className="text-[#A1A1A1] text-sm hover:text-[#39FF14] transition-colors">{cat.label}</Link>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14] mb-4">Account</h4>
                <div className="flex flex-col gap-2">
                  <Link to="/login" className="text-[#A1A1A1] text-sm hover:text-[#39FF14] transition-colors">Sign In</Link>
                  <Link to="/register" className="text-[#A1A1A1] text-sm hover:text-[#39FF14] transition-colors">Register</Link>
                  <Link to="/orders" className="text-[#A1A1A1] text-sm hover:text-[#39FF14] transition-colors">Orders</Link>
                  <Link to="/contact" className="text-[#A1A1A1] text-sm hover:text-[#39FF14] transition-colors">Contact Us</Link>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-[#1A1A1A] mt-12 pt-8">
            <p className="font-mono text-xs text-[#A1A1A1] tracking-wider">&copy; {new Date().getFullYear()} GameBoxMods.com — All rights reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
